import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Paper,
  Grid,
  Container,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";
import * as caseStudyService from "../../../services/CasestudyService";
import Notification from "../../../../../components/mui/Notification";
import debounce from "lodash/debounce";
import { isEpmty } from "../../../../../helper/TextHelper";

export const SituationBackground = ({ caseStudyId, handleUpdatingStatus }) => {
  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
  });
  const [loading, setLoading] = useState(true);
  const [values, setValues] = useState({
    text: "",
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    caseStudyService
      .situationBackground(caseStudyId)
      .then((res) => {
        setValues({
          id: res.data?.id,
          text: res.data?.text,
        });
      })
      .catch((e) => {
        console.error(e);
        setNotify({
          isOpen: true,
          message:
            "Could not get situation and background. Please try again later",
          type: "error",
        });
      })
      .finally(() => {
        setLoading(false);
      });

    // eslint-disable-next-line
  }, []);

  const update = useCallback(
    (id, name, value) => {
      handleUpdatingStatus(true);

      const request = {
        id: id,
        [name]: value,
      };
      caseStudyService
        .updateSituationBackground(request)
        .then((res) => {
          setValues((prevValues) => ({
            ...prevValues,
            id: res.data,
            [name]: value,
          }));
        })
        .catch((e) => {
          console.error(e);
          setNotify({
            isOpen: true,
            message:
              "Could not update situation and background. Please try again later",
            type: "error",
          });
        })
        .finally(() => {
          handleUpdatingStatus(false);
        });
    },
    [handleUpdatingStatus]
  );

  // Debounced save function (memoized to keep reference stable)
  const debouncedSave = useMemo(
    () =>
      debounce((id, name, value) => {
        update(id, name, value);
      }, 2000),
    [update]
  );

  const handleChanges = (e) => {
    const { name, value } = e.target;
    const { id } = values;

    setValues({
      ...values,
      [name]: value,
    });

    if (isEpmty(value)) {
      setErrors((prevValues) => ({
        ...prevValues,
        [name]: "This field can not be empty.",
      }));
    } else {
      clearError(name);
      debouncedSave(id, name, value);
    }
  };

  const clearError = (name) => {
    setErrors((prev) => {
      const updated = { ...prev };
      delete updated[name];
      return updated;
    });
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 2 }}>
      <Notification
        notify={notify}
        setNotify={setNotify}
        autoHideDuration={5000}
      />
      <Paper variant="outlined" sx={{ my: { xs: 1 }, p: { xs: 2 } }}>
        <Grid container spacing={3} sx={{ p: { xs: 1, md: 3 } }}>
          <Grid
            container
            spacing={0}
            justify="center"
            alignItems="center"
            sx={{ mb: 1 }}
          >
            <Grid item>
              <Typography sx={{ fontWeight: "bold" }} variant="h6" gutterBottom>
                Situation and Background
              </Typography>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            {loading ? (
              <Skeleton variant="rounded" sx={{ fontSize: "10rem" }} />
            ) : (
              <TextField
                name="text"
                multiline
                fullWidth
                value={values.text}
                onChange={handleChanges}
                error={!!errors["text"]}
                helperText={errors["text"]}
              />
            )}
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};
